import React from 'react';
import { Button, ConfirmDialog, Alert } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useTranslation } from '../i18n';
import MobileSheet from './MobileSheet';
import useErrorReveal from './useErrorReveal';

/**
 * A task, as a sheet: the commit row, the dirty guard and the failure line.
 *
 * Every mobile form in this app is one of these, which is what makes three
 * rules true everywhere instead of per screen.
 *
 * **The submit docks at the bottom and is padded for the keyboard.** A
 * `position: fixed` footer inside a `100dvh` sheet still ends up under the
 * iOS keyboard, because every CSS viewport unit describes the LAYOUT viewport
 * and the keyboard is painted over it. `MobileSheet` subtracts the measured
 * inset; this component only has to put the row in the footer slot.
 *
 * **A form reports inline, never by toast.** The operator is looking at the
 * sheet; a message that appears behind it and expires in three seconds is a
 * message they did not read. Lists and background actions toast; forms do not.
 *
 * **Every keystroke survives a failed submit.** The sheet does not clear, does
 * not close and does not reset on an error — a server refusal is not a reason
 * to make someone retype an invoice.
 */
export default function MobileFormSheet({
  open,
  onClose,
  title,
  children,
  /** Set true while the form has unsaved changes; guards the close. */
  dirty = false,
  onSubmit,
  submitLabel,
  /** Blocks the commit — a form that is not yet valid. */
  submitDisabled = false,
  submitting = false,
  /** Rendered as an inline Alert above the fields. A string or a node. */
  error,
  /** Secondary action beside the submit; omit for a single-button form. */
  secondary,
  fullHeight = true,
  snap = 0.92,
  'data-testid': testId,
}) {
  const { t } = useTranslation();
  const [confirmLeave, setConfirmLeave] = React.useState(false);

  /**
   * The commit button is docked in the footer and the error renders at the top
   * of the scrolling body, so a refusal lands off-screen for anyone who scrolled
   * down to reach the button — it reads as «nothing happened».
   *
   * The counter is what makes a REPEATED refusal work: the same message twice is
   * the same string, so an effect watching only the text fires once and the
   * second tap looks dead again.
   */
  const errorRef = React.useRef(null);
  const [attempt, setAttempt] = React.useState(0);
  useErrorReveal(errorRef, error, attempt);

  const submit = (e) => {
    setAttempt((n) => n + 1);
    return onSubmit?.(e);
  };

  const requestClose = () => {
    if (dirty && !submitting) { setConfirmLeave(true); return; }
    onClose();
  };

  return (
    <>
      <MobileSheet
        open={open}
        onClose={requestClose}
        title={title}
        fullHeight={fullHeight}
        snap={snap}
        data-testid={testId}
        footer={(
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Button
              onClick={submit}
              disabled={submitDisabled || submitting}
              style={{ flex: 1, minHeight: '2.75rem' }}
              data-testid={testId ? `${testId}-submit` : undefined}
            >
              {submitting ? t('common.state.saving') : submitLabel || t('common.actions.save')}
            </Button>
            {secondary}
          </div>
        )}
      >
        <div style={{ padding: '0.875rem 1rem', display: 'grid', gap: '0.875rem' }}>
          {error ? (
            <div ref={errorRef}>
              <Alert variant="error" role="alert" data-testid={testId ? `${testId}-error` : undefined}>
                {error}
              </Alert>
            </div>
          ) : null}
          {children}
        </div>
      </MobileSheet>

      <ConfirmDialog
        open={confirmLeave}
        onOpenChange={(v) => { if (!v) setConfirmLeave(false); }}
        title={t('common.confirm.unsavedTitle')}
        description={t('common.confirm.unsavedChanges')}
        confirmLabel={t('common.confirm.leaveWithoutSaving')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => { setConfirmLeave(false); onClose(); }}
      />
    </>
  );
}

/** One labelled field. Full width, one per row — never a two-column grid. */
export function MobileField({ label, hint, error, children, htmlFor }) {
  return (
    <div style={{ display: 'grid', gap: '0.3125rem' }}>
      {label ? (
        <label
          htmlFor={htmlFor}
          style={{ fontSize: '0.75rem', fontWeight: 600, color: C.textSecondary }}
        >
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <span role="alert" style={{ fontSize: '0.6875rem', color: C.red }}>{error}</span>
      ) : null}
      {!error && hint ? (
        <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{hint}</span>
      ) : null}
    </div>
  );
}
